import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Channel } from './OrderQueue';
export interface ReasonPolicy {
  id: string;
  label: string;
  requiresFreeText: boolean;
}
export interface OperationalSettings {
  rejectReasons: ReasonPolicy[];
  cancelReasons: ReasonPolicy[];
  holdReasons: ReasonPolicy[];
  returnReasons: ReasonPolicy[];
  slaMinutesByChannel: Record<Channel, number>;
}
export const DEFAULT_OPERATIONAL_SETTINGS: OperationalSettings = {
  rejectReasons: [{
    id: 'reject-stock',
    label: 'Out of Stock',
    requiresFreeText: false
  }, {
    id: 'reject-rx',
    label: 'Prescription Invalid',
    requiresFreeText: false
  }, {
    id: 'reject-controlled',
    label: 'Controlled Substance Restriction',
    requiresFreeText: false
  }, {
    id: 'reject-closing',
    label: 'Store Closing Soon',
    requiresFreeText: false
  }, {
    id: 'reject-area',
    label: 'Delivery Area Not Covered',
    requiresFreeText: false
  }, {
    id: 'reject-other',
    label: 'Other',
    requiresFreeText: true
  }],
  cancelReasons: [{
    id: 'cancel-stock',
    label: 'Out of Stock',
    requiresFreeText: false
  }, {
    id: 'cancel-rx',
    label: 'Prescription Invalid',
    requiresFreeText: false
  }, {
    id: 'cancel-controlled',
    label: 'Controlled Substance Restriction',
    requiresFreeText: false
  }, {
    id: 'cancel-closing',
    label: 'Store Closing Soon',
    requiresFreeText: false
  }, {
    id: 'cancel-area',
    label: 'Delivery Area Not Covered',
    requiresFreeText: false
  }, {
    id: 'cancel-other',
    label: 'Other',
    requiresFreeText: true
  }],
  holdReasons: [{
    id: 'hold-unreachable',
    label: 'Customer Unreachable',
    requiresFreeText: false
  }, {
    id: 'hold-address',
    label: 'Incorrect Address',
    requiresFreeText: false
  }, {
    id: 'hold-rx',
    label: 'Prescription Not Valid',
    requiresFreeText: false
  }, {
    id: 'hold-other',
    label: 'Other',
    requiresFreeText: true
  }],
  returnReasons: [{
    id: 'return-damaged',
    label: 'Damaged',
    requiresFreeText: false
  }, {
    id: 'return-wrong',
    label: 'Wrong Item Received',
    requiresFreeText: false
  }, {
    id: 'return-expired',
    label: 'Expired Product',
    requiresFreeText: false
  }, {
    id: 'return-unneeded',
    label: 'No longer needed',
    requiresFreeText: false
  }, {
    id: 'return-other',
    label: 'Other',
    requiresFreeText: true
  }],
  slaMinutesByChannel: {
    Website: 15,
    Aggregator: 12,
    'Yaser eRx': 10,
    'Walk-in': 10,
    'Insurance Platform': 15
  }
};
interface SettingsPanelProps {
  settings: OperationalSettings;
  onChange: (settings: OperationalSettings) => void;
}
type ReasonListKey = 'rejectReasons' | 'cancelReasons' | 'holdReasons' | 'returnReasons';
const reasonSections: {
  key: ReasonListKey;
  title: string;
  description: string;
}[] = [{
  key: 'rejectReasons',
  title: 'Reject reasons',
  description: 'Reasons that return a pending order to system routing.'
}, {
  key: 'cancelReasons',
  title: 'Cancel reasons',
  description: 'Reasons shown when cancelling an order.'
}, {
  key: 'holdReasons',
  title: 'Hold reasons',
  description: 'Reasons used when pausing an order.'
}, {
  key: 'returnReasons',
  title: 'Return reasons',
  description: 'Reasons offered when starting a return.'
}];
const channels: Channel[] = ['Website', 'Aggregator', 'Yaser eRx', 'Walk-in', 'Insurance Platform'];
export function SettingsPanel({
  settings,
  onChange
}: SettingsPanelProps) {
  const [draftLabels, setDraftLabels] = useState<Record<ReasonListKey, string>>({
    rejectReasons: '',
    cancelReasons: '',
    holdReasons: '',
    returnReasons: ''
  });
  const updateReasons = (key: ReasonListKey, nextReasons: ReasonPolicy[]) => {
    onChange({
      ...settings,
      [key]: nextReasons
    });
  };
  const addReason = (key: ReasonListKey) => {
    const label = draftLabels[key].trim();
    if (!label) return;
    updateReasons(key, [...settings[key], {
      id: `${key}-${Date.now()}`,
      label,
      requiresFreeText: false
    }]);
    setDraftLabels(previous => ({
      ...previous,
      [key]: ''
    }));
  };
  return <section className="mx-auto w-full max-w-6xl space-y-6 pb-12" data-id="element-767" data-mp="17txbuleej5k7">
      <div data-id="element-768" data-mp="1kkfs1t4iu0wp">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-769" data-mp="1kkpi1gpkg82e">Operational Settings</h2>
        <p className="text-slate-500" data-id="element-770" data-mp="1phzgtmyuhw2w">Manage local reason policies and incoming-order acceptance targets.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-771" data-mp="1uh2bqel42lx5">
        {reasonSections.map(section => <article key={section.key} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-772" data-mp="1za56ur5je3fg">
            <div className="mb-5" data-id="element-773" data-mp="247ds50lzenkx">
              <h3 className="font-bold text-slate-900" data-id="element-774" data-mp="2898tu67tr2z8">{section.title}</h3>
              <p className="mt-1 text-sm text-slate-500" data-id="element-775" data-mp="28bbpb5rqxobc">{section.description}</p>
            </div>
            <div className="space-y-2" data-id="element-776" data-mp="2d68we75gtnll">
              {settings[section.key].map(reason => <div key={reason.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3" data-id="element-777" data-mp="2i5c2lux6lzv5">
                  <input value={reason.label} onChange={event => updateReasons(section.key, settings[section.key].map(item => item.id === reason.id ? {
              ...item,
              label: event.target.value
            } : item))} aria-label={`${section.title} label`} className="min-w-0 flex-1 bg-transparent text-sm font-bold text-slate-700 outline-none" data-id="element-778" data-mp="nt5gw26nw0ol" />
                  <label className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium text-slate-600" data-id="element-779" data-mp="1m1dmp8evrkv">
                    <input type="checkbox" checked={reason.requiresFreeText} onChange={event => updateReasons(section.key, settings[section.key].map(item => item.id === reason.id ? {
                ...item,
                requiresFreeText: event.target.checked
              } : item))} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-780" data-mp="z88j01cjhnya" />
                    Requires free text
                  </label>
                  <button onClick={() => updateReasons(section.key, settings[section.key].filter(item => item.id !== reason.id))} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Remove ${reason.label}`} data-id="element-781" data-mp="3505jvqncgtew">
                    <Trash2 className="h-4 w-4" data-id="element-782" data-mp="1s2wxp0mor9ch" />
                  </button>
                </div>)}
            </div>
            <div className="mt-4 flex gap-2" data-id="element-783" data-mp="1ddh5rhbf4xv7">
              <input value={draftLabels[section.key]} onChange={event => setDraftLabels(previous => ({
            ...previous,
            [section.key]: event.target.value
          }))} onKeyDown={event => {
            if (event.key === 'Enter') addReason(section.key);
          }} placeholder="Add a reason" className="min-w-0 flex-1 rounded-xl border-2 border-slate-100 bg-slate-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-784" data-mp="1dcfx1jhvkngn" />
              <button onClick={() => addReason(section.key)} className="inline-flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-900" data-id="element-785" data-mp="fs65k1pkavlf">
                <Plus className="h-4 w-4" data-id="element-786" data-mp="pr5i3vdg7bdp" />
                Add
              </button>
            </div>
          </article>)}
      </div>

      <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-787" data-mp="kqhgygvi9e5r">
        <div className="mb-5" data-id="element-788" data-mp="yq0p39obgpi2">
          <h3 className="font-bold text-slate-900" data-id="element-789" data-mp="yp7ge35srpxa">Acceptance SLA by channel</h3>
          <p className="mt-1 text-sm text-slate-500" data-id="element-790" data-mp="unvwme9ltyx0">Applied to locally created incoming orders. Existing orders keep their assigned deadline.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5" data-id="element-791" data-mp="13jblegvi5nvq">
          {channels.map(channel => <label key={channel} className="rounded-xl border border-slate-100 bg-slate-50 p-4" data-id="element-792" data-mp="1ig2mqvoql9nz">
              <span className="block text-xs font-bold text-slate-600" data-id="element-793" data-mp="1di15mgavfkf5">{channel}</span>
              <div className="mt-2 flex items-center gap-2" data-id="element-794" data-mp="3u8i0dcsqr8kb">
                <input type="number" min="1" value={settings.slaMinutesByChannel[channel]} onChange={event => onChange({
              ...settings,
              slaMinutesByChannel: {
                ...settings.slaMinutesByChannel,
                [channel]: Math.max(1, Number(event.target.value) || 1)
              }
            })} className="min-w-0 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-blue-500" aria-label={`${channel} SLA minutes`} data-id="element-795" data-mp="32yly1zcov74" />
                <span className="text-xs text-slate-500" data-id="element-796" data-mp="3lf4vrdlbeiie">min</span>
              </div>
            </label>)}
        </div>
      </article>
    </section>;
}